"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { Search, Loader2, ArrowUp, MessageCircle, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useDebounce } from "@/lib/hooks/use-debounce";
import {
  unifiedSearch,
  type ProductSearchItem,
  type UserSummary,
  getPlaceholderLogo,
} from "@/lib/services/productService";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";

export function SearchBar() {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useState<ProductSearchItem[]>([]);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const debouncedQuery = useDebounce(query, 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const hasResults = products.length > 0 || users.length > 0;
  const hasQuery = debouncedQuery.trim().length > 0;

  // Perform search when debounced query changes
  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setProducts([]);
      setUsers([]);
      setIsOpen(false);
      return;
    }

    let cancelled = false;

    const doSearch = async () => {
      setIsLoading(true);
      const result = await unifiedSearch(debouncedQuery);

      if (cancelled) return;

      if (result.success && result.data) {
        setProducts(result.data.products);
        setUsers(result.data.users);
        setIsOpen(true);
      }
      setIsLoading(false);
    };

    doSearch();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close dropdown on Escape
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  }, []);

  const handleClear = useCallback(() => {
    setQuery("");
    setProducts([]);
    setUsers([]);
    setIsOpen(false);
    inputRef.current?.focus();
  }, []);

  const handleResultClick = useCallback(() => {
    setIsOpen(false);
    setQuery("");
  }, []);

  return (
    <div ref={containerRef} className="relative hidden md:block w-full max-w-sm">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
      <Input
        ref={inputRef}
        type="text"
        placeholder="Поиск продуктов, основателей..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => hasQuery && hasResults && setIsOpen(true)}
        onKeyDown={handleKeyDown}
        className="pl-9 pr-8 h-9 rounded-full"
      />
      {query && (
        <button
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          {isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <X className="h-3.5 w-3.5" />
          )}
        </button>
      )}

      {/* Search Results Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-popover border border-border rounded-xl overflow-hidden z-50">
          {!hasResults && !isLoading ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              Ничего не найдено по запросу «{debouncedQuery}»
            </div>
          ) : (
            <div className="max-h-[400px] overflow-y-auto">
              {/* Products Section */}
              {products.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Продукты
                  </div>
                  {products.map((product) => (
                    <Link
                      key={product.id}
                      href={`/products/${product.slug}`}
                      onClick={handleResultClick}
                      className="flex items-center gap-3 px-3 py-2.5 hover:bg-accent transition-colors"
                    >
                      <Image
                        src={product.logoUrl || getPlaceholderLogo()}
                        alt={product.name}
                        width={32}
                        height={32}
                        className="rounded-lg object-cover flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {product.name}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {product.tagline}
                        </div>
                      </div>
                      <div className="flex items-center gap-2.5 text-xs text-muted-foreground flex-shrink-0">
                        <span className="flex items-center gap-0.5">
                          <ArrowUp className="h-3 w-3" />
                          {product.voteCount}
                        </span>
                        <span className="flex items-center gap-0.5">
                          <MessageCircle className="h-3 w-3" />
                          {product.commentCount}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {/* Users Section */}
              {users.length > 0 && (
                <div>
                  {products.length > 0 && (
                    <div className="border-t border-border" />
                  )}
                  <div className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Пользователи
                  </div>
                  {users.map((user) => (
                    <Link
                      key={user.id}
                      href={user.username ? `/u/${user.username}` : '#'}
                      onClick={handleResultClick}
                      className="flex items-center gap-3 px-3 py-2.5 hover:bg-accent transition-colors"
                    >
                      <Avatar className="h-8 w-8 flex-shrink-0">
                        <AvatarImage src={user.avatarUrl || undefined} alt={user.name} />
                        <AvatarFallback className="text-xs">
                          {user.name.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {user.name}
                        </div>
                        {user.username && (
                          <div className="text-xs text-muted-foreground">
                            @{user.username}
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function SearchButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useState<ProductSearchItem[]>([]);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const debouncedQuery = useDebounce(query, 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  const hasResults = products.length > 0 || users.length > 0;

  // Perform search when debounced query changes
  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setProducts([]);
      setUsers([]);
      return;
    }

    let cancelled = false;

    const doSearch = async () => {
      setIsLoading(true);
      const result = await unifiedSearch(debouncedQuery);

      if (cancelled) return;

      if (result.success && result.data) {
        setProducts(result.data.products);
        setUsers(result.data.users);
      }
      setIsLoading(false);
    };

    doSearch();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setQuery("");
    setProducts([]);
    setUsers([]);
  }, []);

  const handleResultClick = useCallback((href: string) => {
    handleClose();
    router.push(href);
  }, [handleClose, router]);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleClose]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden rounded-full"
        aria-label="Search"
        onClick={() => setIsOpen(true)}
      >
        <Search className="h-5 w-5" />
      </Button>

      {/* Mobile Search Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 bg-background md:hidden">
          <div className="flex flex-col h-full">
            {/* Search Header */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
              <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <Input
                ref={inputRef}
                type="text"
                placeholder="Поиск продуктов, основателей..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="border-0 shadow-none focus-visible:ring-0 px-0 h-8"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="flex-shrink-0 text-muted-foreground"
              >
                Отмена
              </Button>
            </div>

            {/* Search Results */}
            <div className="flex-1 overflow-y-auto">
              {isLoading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}

              {!isLoading && debouncedQuery.trim() && !hasResults && (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Ничего не найдено по запросу «{debouncedQuery}»
                </div>
              )}

              {!isLoading && hasResults && (
                <div>
                  {/* Products */}
                  {products.length > 0 && (
                    <div>
                      <div className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Продукты
                      </div>
                      {products.map((product) => (
                        <button
                          key={product.id}
                          onClick={() => handleResultClick(`/products/${product.slug}`)}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-accent transition-colors w-full text-left"
                        >
                          <Image
                            src={product.logoUrl || getPlaceholderLogo()}
                            alt={product.name}
                            width={36}
                            height={36}
                            className="rounded-lg object-cover flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-foreground truncate">
                              {product.name}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {product.tagline}
                            </div>
                          </div>
                          <div className="flex items-center gap-2.5 text-xs text-muted-foreground flex-shrink-0">
                            <span className="flex items-center gap-0.5">
                              <ArrowUp className="h-3 w-3" />
                              {product.voteCount}
                            </span>
                            <span className="flex items-center gap-0.5">
                              <MessageCircle className="h-3 w-3" />
                              {product.commentCount}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Users */}
                  {users.length > 0 && (
                    <div>
                      {products.length > 0 && (
                        <div className="border-t border-border" />
                      )}
                      <div className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Пользователи
                      </div>
                      {users.map((user) => (
                        <button
                          key={user.id}
                          onClick={() => handleResultClick(user.username ? `/u/${user.username}` : '#')}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-accent transition-colors w-full text-left"
                        >
                          <Avatar className="h-9 w-9 flex-shrink-0">
                            <AvatarImage src={user.avatarUrl || undefined} alt={user.name} />
                            <AvatarFallback className="text-xs">
                              {user.name.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-foreground truncate">
                              {user.name}
                            </div>
                            {user.username && (
                              <div className="text-xs text-muted-foreground">
                                @{user.username}
                              </div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
